import React, { useState, useEffect } from 'react';
import './brandmanager.css';
import { useNavigate } from 'react-router-dom';
import BrandManagerShell from './components/BrandManagerShell';
import locationsIcon from './images/icons/locations-icon.svg';
import closeIcon from './images/icons/close-icon.svg';
import { useBrand } from './context/BrandContext';
import apiService from './services/api';
import {
  buildBrandManagerCards,
  clearLegacyMockManagedBrands,
  clearPendingAuthorizationBrands,
} from './lib/brandManagerStore';
import MaalBadge from './components/MaalBadge';

function unwrapData(payload) {
  return payload?.data ?? payload;
}

const BrandManager = () => {
  const navigate = useNavigate();
  const { updateBrand } = useBrand();
  const [masterAccounts, setMasterAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      clearLegacyMockManagedBrands();
      clearPendingAuthorizationBrands();
      try {
        const [catalogRaw, mineRaw] = await Promise.all([
          apiService.listBrandCatalog(),
          apiService.listMyBrands().catch(() => ({ brandMemberships: [] })),
        ]);
        if (cancelled) return;
        const catalog = unwrapData(catalogRaw)?.brands || [];
        const memberships = unwrapData(mineRaw)?.brandMemberships || [];
        setMasterAccounts(
          buildBrandManagerCards({
            catalogBrands: catalog,
            memberships,
          }),
        );
      } catch (err) {
        if (!cancelled) {
          setLoadError(err?.message || 'Failed to load brands');
          setMasterAccounts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAccountClick = (accountId) => {
    const selectedAccount = masterAccounts.find((account) => account.id === accountId);
    if (!selectedAccount) return;

    const brandInfo = {
      name: selectedAccount.name,
      type: selectedAccount.type,
      id: selectedAccount.id,
      logoUrl: selectedAccount.logoUrl || null,
    };
    updateBrand(brandInfo);
    navigate(`/master-account/locations`);
  };

  const handleRemoveBrand = async (e, account) => {
    e.stopPropagation();
    if (!account?.id || removingId) return;
    const ok = window.confirm(
      `Remove "${account.name}" from your Brand Manager?\n\nYou can add it again later from Add New Brand.`,
    );
    if (!ok) return;
    setRemovingId(account.id);
    try {
      await apiService.leaveBrand(account.id);
      setMasterAccounts((prev) => prev.filter((b) => b.id !== account.id));
    } catch (err) {
      window.alert(err?.message || `Failed to remove ${account.name}`);
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <BrandManagerShell>
      <div className="brand-manager-header">
        <h1>Brand Manager</h1>
        <p className="subtitle">Select a brand to manage</p>
      </div>

      {loading ? (
        <p className="subtitle">Loading brands…</p>
      ) : loadError ? (
        <p className="subtitle">{loadError}</p>
      ) : masterAccounts.length === 0 ? (
        <p className="subtitle">
          No brands on your Brand Manager yet. Use Add New Brand to search
          the platform catalog.
        </p>
      ) : (
        <div className="accounts-grid">
          {masterAccounts.map((account) => {
            return (
              <div
                key={account.id}
                className="account-card"
                onClick={() => handleAccountClick(account.id)}
              >
                <div className="card-header">
                  <div className="company-avatar">
                    {account.logoUrl ? (
                      <img src={account.logoUrl} alt="" />
                    ) : (
                      "🏢"
                    )}
                  </div>
                  <h2 className="company-name">{account.name}</h2>
                </div>
                <MaalBadge accessLevel={account.type} />
                <div className="card-details">
                  <div className="detail-item">
                    <label>Role:</label>
                    <p>{account.role}</p>
                  </div>
                  <div className="detail-item">
                    <label>Last Accessed:</label>
                    <p>{account.lastAccessed}</p>
                  </div>
                  <div className="detail-item">
                    <label>Locations:</label>
                    <p>{account.locations}</p>
                  </div>
                  <div className="detail-item">
                    <label>Zone(s):</label>
                    <p>{account.zones}</p>
                  </div>
                  <div className="detail-item">
                    <label>Billing Cycle:</label>
                    <p>{account.billingCycle}</p>
                  </div>
                </div>
                <div className="card-separator"></div>
                <div className="card-icons">
                  <img src={locationsIcon} alt="Locations" />
                  <div
                    className="llt-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      const brandInfo = {
                        name: account.name,
                        type: account.type,
                        id: account.id,
                        logoUrl: account.logoUrl || null,
                      };
                      updateBrand(brandInfo);
                      navigate(`/llt/${account.id}`);
                    }}
                    title="Locations Logistic Template (LLT)"
                  >
                    LLT
                  </div>
                  <button
                    type="button"
                    className="remove-brand-icon"
                    onClick={(e) => handleRemoveBrand(e, account)}
                    disabled={removingId === account.id}
                    title="Remove brand"
                    aria-label={`Remove ${account.name}`}
                  >
                    <img src={closeIcon} alt="" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </BrandManagerShell>
  );
};

export default BrandManager;
